import React from "react";
import { Box, CircularProgress, Stack, Tooltip, Typography } from "@mui/material";
import { useNavigate, Navigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { useQueryParam } from "./utils/useQueryParam";
import SearchBar from "../../common/SearchBar/SearchBar";
import ViewTickerLoader from "./ViewTickerLoader";
import Logo from "../../shared/Logo";
import { useChartsWatchlist } from "./hooks/useChartsWatchlist";
import { useMembership, FREE_TIER_TICKER } from "../../../contexts/MembershipContext";
import { MOBILE_TOPBAR_HEIGHT } from "../../../contexts/SidebarContext";
import { isNative } from "../../../lib/platform";
import { SERIF_DISPLAY } from "../../Chat/ChatHero";
import { heroRise } from "../../Chat/chatMotion";
import {
  NativeLandingContainer,
  NativeLandingHeader,
  NativeListRow,
  NativeSectionLabel,
} from "../../Native/NativeLanding";

/** Matches the chat hero's TALK_ABOUT_LIMIT so the tile rows line up across tabs. */
const RESEARCH_TILE_LIMIT = 6;

// Popular stocks fallback data — logos bundled in public/stock-logos (no
// CDN round-trip/flicker; this list is static so the assets can be too)
const popularStocks = [
  { symbol: "AAPL", name: "Apple", logo: "/stock-logos/AAPL.png" },
  { symbol: "NVDA", name: "Nvidia", logo: "/stock-logos/NVDA.png" },
  { symbol: "ASTS", name: "AST SpaceMobile", logo: "/stock-logos/ASTS.png" },
  { symbol: "RKLB", name: "Rocket Lab", logo: "/stock-logos/RKLB.png" },
  { symbol: "NBIS", name: "Nebius Group", logo: "/stock-logos/NBIS.png" },
];

const ChartsPage: React.FC = () => {
  const ticker = useQueryParam("ticker");
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { isMembershipActive } = useMembership();

  // Fetch user's limited, shuffled watchlist for charts page
  const {
    data: chartsWatchlistData,
    isLoading: isChartsWatchlistLoading,
    error: chartsWatchlistError,
  } = useChartsWatchlist();

  if (ticker) {
    if (!isMembershipActive && ticker.toUpperCase() !== FREE_TIER_TICKER) {
      return <Navigate to="/dashboard" replace />;
    }
    return <ViewTickerLoader />;
  }

  const handleStockClick = (symbol: string) => {
    navigate(`/charts?ticker=${symbol}`);
  };

  // If watchlist fails to load or is empty, fall back to popular stocks
  const hasChartsWatchlistData =
    !chartsWatchlistError &&
    chartsWatchlistData?.data &&
    chartsWatchlistData.data.length > 0;
  const stocksToShow = hasChartsWatchlistData
    ? chartsWatchlistData.data.map((item) => ({
        symbol: item.ticker,
        name: item.companyName,
        logo: item.logo,
      }))
    : popularStocks;
  const sectionTitle = hasChartsWatchlistData ? "From your watchlist" : "Popular Stocks";

  // Native app: compact left-aligned header (no logo), search, and dense
  // company rows — vertically centred between the app bars.
  if (isNative()) {
    return (
      <NativeLandingContainer>
        <NativeLandingHeader
          title="Research"
          subtitle="Deep-dive any company's price and financials"
        />

        <Box data-tour="charts-search">
          <SearchBar
            placeholder="Search any stock ticker or company..."
            width="100%"
            variant="chatgpt"
            disabled={!isMembershipActive}
          />
        </Box>

        <NativeSectionLabel>{sectionTitle}</NativeSectionLabel>

        {isChartsWatchlistLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={32} sx={{ color: "#22c55e" }} />
          </Box>
        )}
        {!isChartsWatchlistLoading && (
          <Stack spacing={1.25}>
            {stocksToShow.map((stock) => {
              const isAccessible =
                isMembershipActive || stock.symbol.toUpperCase() === FREE_TIER_TICKER;
              return (
                <NativeListRow
                  key={stock.symbol}
                  leading={
                    <Logo src={stock.logo} alt={`${stock.name} logo`} ticker={stock.symbol} size={28} />
                  }
                  title={stock.name}
                  subtitle={stock.symbol}
                  onClick={() => handleStockClick(stock.symbol)}
                  disabled={!isAccessible}
                />
              );
            })}
          </Stack>
        )}
      </NativeLandingContainer>
    );
  }

  // Web + mobile web: mirrors the Insight AI hero — same serif heading,
  // entrance stagger, logo-tile row AND viewport math (chat subtracts the
  // mobile top bar from 100dvh), so switching tabs never shifts the layout.
  return (
    <Box
      sx={{
        height: { xs: `calc(100dvh - ${MOBILE_TOPBAR_HEIGHT}px)`, lg: "100vh" },
        overflowY: "auto",
        backgroundColor: isDark ? "#1a1a1a" : "#ffffff",
      }}
    >
      <Box
        sx={{
          minHeight: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          width: "100%",
          maxWidth: 680,
          mx: "auto",
          px: { xs: 2, md: 3 },
          py: { xs: 4, md: 6 },
        }}
      >
        <Box sx={{ mb: 3, ...heroRise(0) }}>
          <img
            src={isDark ? "/insightLongLogoPrimaryWhiteText.svg" : "/insightLongLogoPrimary.svg"}
            alt="Insight Analytics"
            style={{ height: 48, width: "auto", maxWidth: "100%" }}
          />
        </Box>

        <Typography
          component="h1"
          sx={{
            fontFamily: SERIF_DISPLAY,
            fontWeight: 500,
            fontSize: { xs: "1.75rem", md: "2.25rem" },
            letterSpacing: "-0.01em",
            lineHeight: 1.25,
            color: isDark ? "#ffffff" : "#1d1d1f",
            mb: { xs: 3.5, md: 4.5 },
            ...heroRise(0.05),
          }}
        >
          What would you like to research?
        </Typography>

        <Box data-tour="charts-search" sx={{ width: "100%", ...heroRise(0.14) }}>
          <Tooltip title={!isMembershipActive ? "Available with Insight Pro" : ""} arrow>
            <span style={{ display: "block", width: "100%" }}>
              <SearchBar
                placeholder="Search any stock ticker or company name..."
                width="100%"
                variant="chatgpt"
                disabled={!isMembershipActive}
              />
            </span>
          </Tooltip>
        </Box>

        {/* Compact company tiles, identical to the chat hero's "talk about" row:
            logo only, names in tooltips, one uniform row under the search. */}
        <Box sx={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 1, mt: 2 }}>
          {stocksToShow.slice(0, RESEARCH_TILE_LIMIT).map((stock, index) => {
            const isAccessible =
              isMembershipActive || stock.symbol.toUpperCase() === FREE_TIER_TICKER;
            return (
              <Tooltip
                key={stock.symbol}
                title={isAccessible ? `Research ${stock.name}` : "Available with Insight Pro"}
                arrow
              >
                {/* span so the tooltip still fires on inaccessible tiles */}
                <span>
                  <Box
                    component="button"
                    disabled={!isAccessible}
                    aria-label={`Research ${stock.name}`}
                    onClick={() => handleStockClick(stock.symbol)}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 40,
                      height: 40,
                      p: 0,
                      cursor: isAccessible ? "pointer" : "default",
                      opacity: isAccessible ? 1 : 0.5,
                      pointerEvents: isAccessible ? "auto" : "none",
                      borderRadius: 2,
                      border: isDark
                        ? "1px solid rgba(255, 255, 255, 0.1)"
                        : "1px solid rgba(0, 0, 0, 0.1)",
                      backgroundColor: isDark ? "rgba(255, 255, 255, 0.04)" : "rgba(0, 0, 0, 0.04)",
                      transition: "border-color 0.2s ease, background-color 0.2s ease, transform 0.2s ease",
                      "&:hover": {
                        backgroundColor: isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)",
                        borderColor: isDark ? "rgba(34, 197, 94, 0.45)" : "rgba(34, 197, 94, 0.4)",
                        transform: "scale(1.05)",
                      },
                      "&:active": { transform: "scale(0.97)" },
                      "&:focus-visible": { outline: "2px solid #22c55e", outlineOffset: 2 },
                      ...heroRise(0.2 + index * 0.045),
                    }}
                  >
                    <Logo src={stock.logo} alt={stock.name} ticker={stock.symbol} size={22} />
                  </Box>
                </span>
              </Tooltip>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default ChartsPage;
